import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileText, FolderOpen, Loader2, Receipt } from 'lucide-react';
import { Input } from '../ui/input';
import { fmtEur, fmtDate } from '../paiementsCb/gedUtils';
import { API, openBlob, prevMonth, downloadBlob, PRODUITS_FILTRE } from './api';

const Justificatifs = ({ dossierId, headers, tid }) => {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/comptabilite/justificatifs/${encodeURIComponent(dossierId)}`, { headers }).then((r) => setDocs(r.data.items)).catch(() => setDocs([]));
  }, [dossierId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!docs) return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
  if (!docs.length) return <p className="text-xs text-slate-500">Aucun justificatif.</p>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`justificatifs-${tid}`}>
      {docs.map((d) => (
        <button key={d.ged_id} type="button" onClick={() => openBlob(`/comptabilite/documents/${d.ged_id}/download`, headers)} data-testid={`justificatif-${d.ged_id}`}
          className="inline-flex items-center gap-1 text-[11px] rounded-md border border-slate-200 bg-slate-50 px-2 py-1 hover:bg-slate-100">
          <FileText className="h-3 w-3" /> {d.sous_dossier} · {d.doc_type_label || d.filename}
        </button>
      ))}
    </div>
  );
};

const Ligne = ({ x, i, headers }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="py-2 border-t border-slate-100" data-testid={`recu-row-${i}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="w-20 text-slate-500 tabular-nums">{fmtDate(x.date)}</span>
        <span className="font-medium text-slate-900">{x.membre || '—'}</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{x.produit}</span>
        <span className="text-xs text-slate-500">{x.mode}</span>
        <span className="ml-auto font-semibold tabular-nums">{fmtEur(x.montant)}</span>
        <button type="button" disabled={!x.ged_id} onClick={() => openBlob(`/comptabilite/documents/${x.ged_id}/download`, headers)} data-testid={`recu-open-${i}`}
          className="inline-flex items-center gap-1 text-[11px] rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-800 disabled:opacity-40">
          <Receipt className="h-3 w-3" /> {x.recu || 'Reçu'}
        </button>
        {x.dossier_id && (
          <button type="button" onClick={() => setOpen(!open)} data-testid={`recu-justif-toggle-${i}`}
            className="inline-flex items-center gap-1 text-[11px] rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-50"><FolderOpen className="h-3 w-3" /> Justificatifs</button>
        )}
      </div>
      {open && <div className="mt-2 pl-20"><Justificatifs dossierId={x.dossier_id} headers={headers} tid={i} /></div>}
    </li>
  );
};

export const RecusDuMois = ({ headers }) => {
  const [mois, setMois] = useState(prevMonth());
  const [produit, setProduit] = useState('');
  const [data, setData] = useState(null);
  useEffect(() => {
    let stale = false;
    setData(null);
    axios.get(`${API}/comptabilite/recus`, { headers, params: { mois, produit } })
      .then((r) => { if (!stale) setData(r.data); }).catch(() => { if (!stale) setData({ items: [], error: true }); });
    return () => { stale = true; };
  }, [mois, produit]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="recus-du-mois">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="flex items-center gap-2 font-bold text-slate-900"><Receipt className="h-5 w-5 text-emerald-600" /> Reçus du mois</h3>
        <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="h-8 w-40 text-sm" data-testid="recus-mois-input" />
        <span className="flex gap-1" data-testid="recus-filtre-produit">
          {PRODUITS_FILTRE.map(([k, l]) => (
            <button key={k || 'tous'} type="button" onClick={() => setProduit(k)} data-testid={`recus-produit-${k || 'tous'}`}
              className={`text-xs px-2.5 py-1 rounded-full border ${produit === k ? 'bg-[#0a3d62] text-white border-[#0a3d62]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
          ))}
        </span>
        <button type="button" onClick={() => downloadBlob(`/comptabilite/exports/${mois}/xlsx?produit=${produit}`, headers, `export_comptable_${produit ? `${produit}_` : ''}${mois}.xlsx`)} data-testid="recus-xlsx-btn"
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"><Download className="h-3.5 w-3.5" /> Excel</button>
        {data && !data.error && (
          <span className="ml-auto text-sm text-slate-600" data-testid="recus-synthese">
            {data.items.length} encaissement(s) · CB {fmtEur(data.total_cb)} · SEPA {fmtEur(data.total_sepa)} · <strong>Total {fmtEur(data.total)}</strong>
          </span>
        )}
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.items.length === 0
        ? <p className="text-sm text-slate-500 py-4" data-testid="recus-empty">Aucun encaissement sur ce mois.</p>
        : <ul>{data.items.map((x, i) => <Ligne key={i} x={x} i={i} headers={headers} />)}</ul>}
    </section>
  );
};
